"use client"

import { cn } from "@/utils/classnames"
import { useState, forwardRef } from "react"
import { Text } from "./Text"

interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  hasBorder?: boolean
  label?: string
  labelVariant?: "body2" | "label"
  labelClassName?: string
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ className, hasBorder = true, label, labelVariant = "label", labelClassName, ...rest }, ref) => {
    const [isFocused, setIsFocused] = useState(false)

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(true)
      rest.onFocus?.(e)
    }

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(false)
      rest.onBlur?.(e)
    }

    return (
      <div className="flex w-full flex-col">
        {label && (
          <div className={cn("mb-2", labelClassName)}>
            <Text variant={labelVariant}>{label}</Text>
          </div>
        )}
        <textarea
          ref={ref}
          className={cn(
            "resize-none rounded-md border bg-transparent p-4 outline-none ring-transparent text-body1-md placeholder:text-fg-3 focus:outline-none focus-visible:outline-none lg:text-body1-lg",
            isFocused ? "border-primary-3" : "border-bg-3",
            className
          )}
          {...rest}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        {rest.maxLength && (
          <div className="mt-2 flex justify-end">
            <Text variant="body2">{`${rest.value?.toString()?.length} / ${rest.maxLength}`}</Text>
          </div>
        )}
      </div>
    )
  }
)

TextArea.displayName = "TextArea"
